import { FormikCheckboxField } from '@superdispatch/forms';
import { Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { castAPIData } from 'shared/api/APISchema';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikDrawer,
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { z } from 'zod';
import { loadPickupEditSchema } from '../data/LoadActionsDTO';
import {
  formatLoadStatus,
  isEmptyLoadLeg,
  loadVenueSchema,
} from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadDetails, useLoadsAPI, useLoadsCache } from '../data/LoadsAPI';
import { useLoadStageFromBackURL } from '../view/ViewLoadHeaderActions';
import { LoadPickupForm } from './LoadPickupForm';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const formSchema = loadPickupEditSchema.extend({
  $copyToCustomer: z.boolean().default(false),
});

interface EditLoadPickupDrawerContentProps {
  loadGUID: string;
  onClose: () => void;
}

function EditLoadPickupDrawerContent({
  onClose,
  loadGUID,
}: EditLoadPickupDrawerContentProps) {
  const isRestrictedAddressEditing = useFlag(
    'carrier_restrict_shipper_address_editing',
  );
  const { isSubhauled } = useSubhaulLoad();
  const { data } = useLoadDetails(loadGUID, { isSubhauled });
  const { invalidateLoads } = useLoadsCache();
  const { editLoadPickup, editLoadCustomer } = useLoadsAPI();
  const loadStage = useLoadStageFromBackURL();
  const formik = useAppFormik({
    initialValues: data?.pickup,
    validationSchema: formSchema,
    async onSubmit({ $copyToCustomer, ...values }) {
      const response = await editLoadPickup(loadGUID, values);

      if ($copyToCustomer) {
        await editLoadCustomer(loadGUID, {
          save_customer_contact: false,
          venue: castAPIData(loadVenueSchema, values.venue),
        });
      }

      return response;
    },
    onSubmitSuccess() {
      onClose();
      invalidateLoads();

      trackLoadsEvent({
        name: 'Carrier Edited Load',
        utm_medium: loadStage,
        utm_content: 'View Page',
        load_status: formatLoadStatus(data?.status),
        is_created_by_broker: !!data?.is_created_by_broker,
        pickup_information:
          !data?.pickup || isEmptyLoadLeg(data.pickup) ? 'Added' : 'Edited',
      });
    },
    getSuccessMessage(_, { $copyToCustomer }) {
      return $copyToCustomer
        ? 'Successfully changed pickup and customer information'
        : 'Successfully changed pickup information';
    },
    getErrorMessage({ message }) {
      return message || 'Something went wrong, contact support.';
    },
  });

  return (
    <FormikDrawerContent
      formik={formik}
      title="Pickup Information"
      onClose={onClose}
      actions={<FormikDrawerActions name="loads" permission="can_manage" />}
    >
      <Box maxWidth="424px">
        <Stack space="large">
          {!data?.is_received_via_subhaul && !isSubhauled && (
            <FormikCheckboxField
              name="$copyToCustomer"
              label="Copy to Customer"
              helperText="Customer will have the same information as below"
            />
          )}

          <LoadPickupForm
            isEDI={!!data?.is_edi}
            isSubhauled={data?.is_received_via_subhaul}
            isDisabledAddressDetails={isSubhauled}
            canEditAddress={
              isRestrictedAddressEditing
                ? !data?.is_created_by_broker
                : data?.can_edit_pickup_address
            }
          />
        </Stack>
      </Box>
    </FormikDrawerContent>
  );
}

interface EditLoadPickupDrawerProps extends EditLoadPickupDrawerContentProps {
  open: boolean;
}

export function EditLoadPickupDrawer({
  open,
  onClose,
  loadGUID,
}: EditLoadPickupDrawerProps) {
  return (
    <FormikDrawer open={open} onClose={onClose}>
      <EditLoadPickupDrawerContent loadGUID={loadGUID} onClose={onClose} />
    </FormikDrawer>
  );
}
